"use client"

import * as React from "react"
import type { Node as TiptapNode } from "@tiptap/pm/model"
import { NodeSelection } from "@tiptap/pm/state"
import { offset } from "@floating-ui/react"
import {
  POST_EDITOR_FONT_FAMILIES,
  POST_EDITOR_HR_COLORS,
  POST_EDITOR_HR_WIDTH_OPTIONS,
  normalizeHrColor,
  normalizeHrWidth,
  postEditorHrClassList,
  postEditorHrColorLabel,
  pathstoneQuoteHasAttribution,
} from "../../../../app/javascript/lib/post_editor_constants.js"
import { resourceRowHasSubtitle } from "../../../../app/javascript/lib/resource_row_paragraph.js"
import { selectionInRestrictedInlineLinkContext } from "../../../../app/javascript/lib/post_editor_paragraph_style.js"

// Hooks
import { useTiptapEditor } from "../../../hooks/use-tiptap-editor"
import { useIsMobile } from "../../../hooks/use-mobile"
import { useUiEditorState } from "../../../hooks/use-ui-editor-state"
import { selectNodeAndHideFloating } from "../../../hooks/use-floating-toolbar-visibility"
import { useImageCaption } from "../../../components/tiptap-ui/image-caption-button"
import {
  IMAGE_UPLOAD_SHORTCUT_KEY,
  useImageUpload,
} from "../../../components/tiptap-ui/image-upload-button"

// Primitive UI Components
import { Button, ButtonGroup } from "../../tiptap-ui-primitive/button"
import { Spacer } from "../../tiptap-ui-primitive/spacer"
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuGroupLabel,
  MenuButton,
} from "../../tiptap-ui-primitive/menu"
import { Combobox, ComboboxList } from "../../tiptap-ui-primitive/combobox"
import { Separator } from "../../tiptap-ui-primitive/separator"

// Tiptap UI
import { useImageDownload } from "../image-download-button"
import {
  DuplicateShortcutBadge,
  useDuplicate,
} from "../duplicate-button"
import {
  CopyToClipboardShortcutBadge,
  useCopyToClipboard,
} from "../copy-to-clipboard-button"
import {
  DeleteNodeShortcutBadge,
  useDeleteNode,
} from "../delete-node-button"
import {
  CopyAnchorLinkShortcutBadge,
  useCopyAnchorLink,
} from "../copy-anchor-link-button"
import { useResetAllFormatting } from "../reset-all-formatting-button"
import { toggleList } from "../list-button/use-list"
import { toggleCodeBlock } from "../code-block-button/use-code-block"
import { toggleBlockquote } from "../blockquote-button/use-blockquote"
import { SlashCommandTriggerButton } from "../slash-command-trigger-button"
// trimmed: remove pro color/ai/transform hooks; we'll implement minimal actions inline

import { DragHandle } from "@tiptap/extension-drag-handle-react"
const DragHandleAny = DragHandle as unknown as React.ComponentType<any>

// Utils
import {
  getNodeDisplayName,
  isTextSelectionValid,
} from "../../../lib/tiptap-collab-utils"

import type { DragContextMenuProps, MenuItemProps, NodeChangeData } from "./drag-context-menu-types"
import { useMenuActionVisibility } from "./drag-context-menu-hooks"

// Icons
import { GripVerticalIcon } from "../../tiptap-icons/grip-vertical-icon"
import { PaintBucketIcon } from "../../tiptap-icons/paint-bucket-icon"
import { ChevronRightIcon } from "../../tiptap-icons/chevron-right-icon"
import { Repeat2Icon } from "../../tiptap-icons/repeat-2-icon"
import { TextColorSmallIcon } from "../../tiptap-icons/text-color-small-icon"
import { TypeIcon } from "../../tiptap-icons/type-icon"
import { LinkIcon } from "../../tiptap-icons/link-icon"
import { ListIcon } from "../../tiptap-icons/list-icon"
import { ListOrderedIcon } from "../../tiptap-icons/list-ordered-icon"
import { BlockquoteIcon } from "../../tiptap-icons/blockquote-icon"
import { CodeBlockIcon } from "../../tiptap-icons/code-block-icon"
import { ArrowDownToLineIcon } from "../../tiptap-icons/arrow-down-to-line-icon"
import { RefreshCcwIcon } from "../../tiptap-icons/refresh-ccw-icon"
import { PencilIcon } from "../../tiptap-icons/pencil-icon"
import { PlusIcon } from "../../tiptap-icons/plus-icon"

const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
} as const

// removed pro transform hook usage; we'll build actions directly

// -----------------------------------------------------------------------------
// Color/variant option lists used by the drag-context Color submenu and the
// nested marker-color submenu inside the OrderedList "Turn Into" entry.
//
// Keep these in sync with the toolbar source-of-truth lists in
//   app/javascript/lib/post_editor_paragraph_style.js
//   app/javascript/lib/post_editor_constants.js
// They're inlined here because the drag-context-menu lives in the `@/`
// package, which has no path alias back into `app/javascript/lib/*`.
// -----------------------------------------------------------------------------
// NOTE: These values mirror POST_EDITOR_HEADING_ACCENTS from app/javascript/lib/post_editor_paragraph_style.js
// Keep values in sync when updating accent colors
const HEADING_ACCENT_OPTIONS: Array<{ id: string; label: string; cssVar: string }> = [
  { id: "black", label: "Black", cssVar: "var(--ps-black)" },
  { id: "gray-07", label: "Gray 07", cssVar: "var(--ps-gray-07)" },
  { id: "plum", label: "Plum", cssVar: "var(--ps-plum)" },
  { id: "red", label: "Red", cssVar: "var(--ps-red)" },
  { id: "gold", label: "Gold", cssVar: "var(--ps-gold)" },
  { id: "moss", label: "Moss", cssVar: "var(--ps-moss)" },
]

// NOTE: These values mirror POST_EDITOR_QUOTE_SWATCHES from app/javascript/lib/post_editor_constants.js
// Keep values in sync when updating quote colors
const QUOTE_VARIANT_OPTIONS: Array<{ id: string; label: string; hex: string }> = [
  { id: "blue", label: "Blue", hex: "#107FAE" },
  { id: "plum", label: "Plum", hex: "#75556A" },
  { id: "black", label: "Black", hex: "#000000" },
  { id: "brick", label: "Brick", hex: "#A8483D" },
]

// NOTE: These values mirror POST_EDITOR_ORDERED_MARKER_COLORS from app/javascript/lib/post_editor_constants.js
// Keep values in sync when updating marker colors
const ORDERED_MARKER_OPTIONS: Array<{ hex: string; label: string }> = [
  { hex: "#000000", label: "Black" },
  { hex: "#9BA854", label: "Moss" },
  { hex: "#A8483D", label: "Brick" },
  { hex: "#F03E3E", label: "Red" },
  { hex: "#61584F", label: "Gray 07" },
  { hex: "#522A45", label: "Plum" },
]

// Categorise a drag-handle node so the menu can render the per-type item set
// the product spec calls for (spacer = delete only, heading/subheading get
// accent color + paragraph-style turn-into, etc.). Anything we don't have a
// specific recipe for falls into 'other' and keeps the legacy behaviour.
type MenuKind =
  | "spacer"
  | "horizontalRule"
  | "heading"
  | "subheading"
  | "caption"
  | "blockquote"
  | "orderedList"
  | "bulletList"
  | "paragraph"
  | "media"
  | "carousel"
  | "resourceRow"
  | "other"

function getNodeMenuKind(node: TiptapNode | null | undefined): MenuKind {
  if (!node) return "other"
  const t = node.type?.name
  if (!t) return "other"
  if (t === "postSpacer") return "spacer"
  if (t === "horizontalRule") return "horizontalRule"
  if (t === "blockquote") return "blockquote"
  if (t === "orderedList") return "orderedList"
  if (t === "bulletList") return "bulletList"
  if (t === "paragraph") {
    const style = (node.attrs as any)?.postTextStyle
    if (style === "heading") return "heading"
    if (style === "subheading") return "subheading"
    if (style === "caption") return "caption"
    return "paragraph"
  }
  if (t === "postImageCarousel") return "carousel"
  if (t === "image" || t === "video" || t === "embedVideo") return "media"
  if (
    t === "downloadRow" ||
    t === "externalLinkRow" ||
    t === "meetingRow" ||
    t === "audioRow" ||
    t === "resourceTable"
  ) {
    return "resourceRow"
  }
  return "other"
}

const BaseMenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  isActive = false,
  shortcutBadge,
}) => (
  <MenuItem
    render={
      <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
    }
    onClick={onClick}
    disabled={disabled}
  >
    <Icon className="tiptap-button-icon" />
    <span className="tiptap-button-text">{label}</span>
    {shortcutBadge}
  </MenuItem>
)

const SubMenuTrigger: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
  isActive?: boolean
}> = ({ icon: Icon, label, children, isActive = false }) => (
  <Menu
    placement="right"
    trigger={
      <MenuItem
        render={
          <MenuButton
            render={
              <Button data-style="ghost" data-active-state={isActive ? "on" : "off"}>
                <Icon className="tiptap-button-icon" />
                <span className="tiptap-button-text">{label}</span>
                <Spacer />
                <ChevronRightIcon className="tiptap-button-icon" />
              </Button>
            }
          />
        }
      />
    }
  >
    <MenuContent portal>
      <ComboboxList style={{ width: "fit-content" }}>{children}</ComboboxList>
    </MenuContent>
  </Menu>
)

// removed highlight/recent colors support to mirror minimal toolbar

// Color swatch (small filled square) used as the leading icon in submenu items.
const ColorSwatchIndicator: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden="true"
    className="tiptap-button-icon"
    style={{
      display: "inline-block",
      width: "12px",
      height: "12px",
      borderRadius: "3px",
      background: color,
      border: "1px solid rgba(0,0,0,0.12)",
      flexShrink: 0,
    }}
  />
)

// "H" / "S" letter-tile preview that mirrors the toolbar's heading-color
// popover (`.ps-accent-tile__letter`). Uses the accent CSS variable as colour.
const HeadingAccentIndicator: React.FC<{ letter: "H" | "S"; cssVar: string }> = ({
  letter,
  cssVar,
}) => (
  <span
    aria-hidden="true"
    className="tiptap-button-icon"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "16px",
      height: "16px",
      color: cssVar,
      fontWeight: 700,
      fontSize: "13px",
      lineHeight: 1,
      flexShrink: 0,
    }}
  >
    {letter}
  </span>
)

// One row in the heading/subheading "Color" submenu — wires `setPostParagraphStyle`
// so the swatch updates the active paragraph's accent attribute (matching the
// toolbar's behaviour) and stays selected via `data-active-state`.
const HeadingAccentMenuItem: React.FC<{
  editor: any
  style: "heading" | "subheading"
  accentId: string
  label: string
  cssVar: string
  isActive: boolean
}> = ({ editor, style, accentId, label, cssVar, isActive }) => {
  const onClick = React.useCallback(() => {
    if (!editor) return
    ;(editor as any)
      .chain()
      .focus()
      .setPostParagraphStyle({ style, accent: accentId })
      .run()
  }, [editor, style, accentId])

  return (
    <MenuItem
      render={
        <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={onClick}
    >
      <HeadingAccentIndicator letter={style === "heading" ? "H" : "S"} cssVar={cssVar} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

// One row in the blockquote "Color" submenu — wires `setPathstoneQuoteVariant`
// so the swatch updates the active blockquote's variant attribute.
// Mini `<hr>` preview using the same class contract as the post editor toolbar.
const HrLinePreview: React.FC<{ color: string; lineWidth: string }> = ({
  color,
  lineWidth,
}) => (
  <span
    aria-hidden="true"
    className="tiptap-button-icon"
    style={{
      display: "inline-flex",
      alignItems: "center",
      width: "20px",
      flexShrink: 0,
    }}
  >
    <hr
      className={postEditorHrClassList(color, lineWidth)}
      style={{ width: "100%", margin: 0, border: "none" }}
    />
  </span>
)

const HrWidthMenuItem: React.FC<{
  editor: any
  nodePos: number
  color: string
  lineWidth: string
  label: string
  isActive: boolean
}> = ({ editor, nodePos, color, lineWidth, label, isActive }) => {
  const onClick = React.useCallback(() => {
    if (!editor || nodePos < 0) return
    try {
      selectNodeAndHideFloating(editor, nodePos)
    } catch {}
    ;(editor as any)
      .chain()
      .focus()
      .updateAttributes("horizontalRule", {
        color: normalizeHrColor(color),
        lineWidth: normalizeHrWidth(lineWidth),
      })
      .run()
  }, [editor, nodePos, color, lineWidth])

  return (
    <MenuItem
      render={
        <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={onClick}
    >
      <HrLinePreview color={color} lineWidth={lineWidth} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const HrColorMenuItem: React.FC<{
  editor: any
  nodePos: number
  lineWidth: string
  hex: string
  label: string
  isActive: boolean
}> = ({ editor, nodePos, lineWidth, hex, label, isActive }) => {
  const onClick = React.useCallback(() => {
    if (!editor || nodePos < 0) return
    try {
      selectNodeAndHideFloating(editor, nodePos)
    } catch {}
    ;(editor as any)
      .chain()
      .focus()
      .updateAttributes("horizontalRule", {
        color: normalizeHrColor(hex),
        lineWidth: normalizeHrWidth(lineWidth),
      })
      .run()
  }, [editor, nodePos, lineWidth, hex])

  return (
    <MenuItem
      render={
        <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={onClick}
    >
      <ColorSwatchIndicator color={hex} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const HorizontalRuleActionGroup: React.FC<{
  editor: any
  node: TiptapNode | null
  nodePos: number
}> = ({ editor, node, nodePos }) => {
  if (getNodeMenuKind(node) !== "horizontalRule") return null

  const currentColor = normalizeHrColor((node?.attrs as { color?: string })?.color)
  const currentWidth = normalizeHrWidth((node?.attrs as { lineWidth?: string })?.lineWidth)

  return (
    <>
      <SubMenuTrigger icon={TypeIcon} label="Size">
        <MenuGroup>
          {POST_EDITOR_HR_WIDTH_OPTIONS.map((opt) => (
            <HrWidthMenuItem
              key={opt.width}
              editor={editor}
              nodePos={nodePos}
              color={currentColor}
              lineWidth={opt.width}
              label={opt.label}
              isActive={currentWidth === opt.width}
            />
          ))}
        </MenuGroup>
      </SubMenuTrigger>
      <SubMenuTrigger icon={PaintBucketIcon} label="Color">
        <MenuGroup>
          {POST_EDITOR_HR_COLORS.map((hex) => (
            <HrColorMenuItem
              key={hex}
              editor={editor}
              nodePos={nodePos}
              lineWidth={currentWidth}
              hex={hex}
              label={postEditorHrColorLabel(hex)}
              isActive={currentColor.toUpperCase() === hex.toUpperCase()}
            />
          ))}
        </MenuGroup>
      </SubMenuTrigger>
    </>
  )
}

const QuoteVariantMenuItem: React.FC<{
  editor: any
  variantId: string
  label: string
  hex: string
  isActive: boolean
}> = ({ editor, variantId, label, hex, isActive }) => {
  const onClick = React.useCallback(() => {
    if (!editor) return
    ;(editor as any).chain().focus().setPathstoneQuoteVariant(variantId).run()
  }, [editor, variantId])

  return (
    <MenuItem
      render={
        <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={onClick}
    >
      <ColorSwatchIndicator color={hex} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

// Color submenu trigger. Hidden for everything except heading / subheading
// (paragraph-accent picker) and blockquote (quote-variant picker). All other
// node types — body/caption paragraph, lists, spacer, media, resource rows —
// no longer get a Color entry per the new spec.
const ColorActionGroup: React.FC<{ editor: any; node: TiptapNode | null }> = ({ editor, node }) => {
  const kind = getNodeMenuKind(node)

  if (kind === "heading" || kind === "subheading") {
    const currentAccent = ((node?.attrs as any)?.postTextAccent || "black") as string
    return (
      <SubMenuTrigger icon={PaintBucketIcon} label="Color">
        <MenuGroup>
          {HEADING_ACCENT_OPTIONS.map((accent) => (
            <HeadingAccentMenuItem
              key={accent.id}
              editor={editor}
              style={kind}
              accentId={accent.id}
              label={accent.label}
              cssVar={accent.cssVar}
              isActive={currentAccent === accent.id}
            />
          ))}
        </MenuGroup>
      </SubMenuTrigger>
    )
  }

  if (kind === "caption") {
    const currentTokenRaw = (editor?.getAttributes?.("psTextColor") as any)?.token
    const currentToken = typeof currentTokenRaw === "string" ? currentTokenRaw.toLowerCase() : null
    const isBlackActive = !currentToken || currentToken === "ps-black" || currentToken === "black"
    const isGray06Active = currentToken === "ps-gray-06" || currentToken === "gray-06"

    return (
      <SubMenuTrigger icon={PaintBucketIcon} label="Color">
        <MenuGroup>
          <BaseMenuItem
            icon={TypeIcon}
            label="Black"
            disabled={!editor}
            isActive={isBlackActive}
            onClick={() => {
              if (!editor) return
              ;(editor as any).chain().focus().unsetMark("psTextColor").run()
            }}
          />
          <BaseMenuItem
            icon={TypeIcon}
            label="Gray 06"
            disabled={!editor}
            isActive={isGray06Active}
            onClick={() => {
              if (!editor) return
              ;(editor as any).chain().focus().setMark("psTextColor", { token: "ps-gray-06" }).run()
            }}
          />
        </MenuGroup>
      </SubMenuTrigger>
    )
  }

  if (kind === "blockquote") {
    const currentVariant = ((node?.attrs as any)?.variant || "black") as string
    return (
      <SubMenuTrigger icon={PaintBucketIcon} label="Color">
        <MenuGroup>
          {QUOTE_VARIANT_OPTIONS.map((v) => (
            <QuoteVariantMenuItem
              key={v.id}
              editor={editor}
              variantId={v.id}
              label={v.label}
              hex={v.hex}
              isActive={currentVariant === v.id}
            />
          ))}
        </MenuGroup>
      </SubMenuTrigger>
    )
  }

  return null
}

const BlockquoteAttributionActionGroup: React.FC<{ editor: any; node: TiptapNode | null }> = ({
  editor,
  node,
}) => {
  if (getNodeMenuKind(node) !== "blockquote") return null
  if (!node || pathstoneQuoteHasAttribution(node)) return null

  const onClick = React.useCallback(() => {
    if (!editor) return
    ;(editor as any).chain().focus().addPathstoneQuoteAttribution().run()
  }, [editor])

  return (
    <BaseMenuItem icon={PlusIcon} label="Add Attribution" onClick={onClick} />
  )
}

const ResourceRowSubtitleActionGroup: React.FC<{
  editor: any
  node: TiptapNode | null
  rowNode?: TiptapNode | null
  rowPos?: number | null
}> = ({ editor, node, rowNode, rowPos }) => {
  const resolvedRow =
    rowNode ||
    (node &&
    ["downloadRow", "externalLinkRow", "meetingRow", "audioRow"].includes(node.type?.name || "")
      ? node
      : null)

  if (!resolvedRow || resourceRowHasSubtitle(resolvedRow)) return null

  const onClick = React.useCallback(() => {
    if (!editor) return
    if (typeof rowPos === 'number') {
      ;(editor as any).commands.addResourceRowSubtitle(rowPos)
    } else {
      ;(editor as any).commands.addResourceRowSubtitle()
    }
  }, [editor, rowPos])

  return <BaseMenuItem icon={PlusIcon} label="Add Subtitle" onClick={onClick} />
}

const FontFamilyMenuItem: React.FC<{ editor: any; value: string; label: string }> = ({ editor, value, label }) => {
  const fontFamilyClassAttrs = editor?.getAttributes('fontFamilyClass') || {};
  const isActive = fontFamilyClassAttrs.fontFamilyClass === value;
  
  const onClick = React.useCallback(() => {
    if (!editor) return
    editor.chain().focus().unsetFontFamilyClass().setFontFamilyClass(value).run()
  }, [editor, value])

  return (
    <MenuItem
      render={
        <Button
          data-style="ghost"
          data-active-state={isActive ? "on" : "off"}
        />
      }
      onClick={onClick}
    >
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const FontFamilyActionGroup: React.FC<{ editor: any; node: TiptapNode | null }> = ({ editor, node }) => {
  // Per the new drag-menu spec, mentioned types (spacer/heading/subheading/
  // blockquote) only show the items the user explicitly listed (Color +
  // Turn Into + Delete combinations). They no longer get a Font submenu.
  // Media + resource + carousel nodes also continue to be hidden as before.
  const kind = getNodeMenuKind(node)
  if (
    kind === "spacer" ||
    kind === "horizontalRule" ||
    kind === "heading" ||
    kind === "subheading" ||
    kind === "caption" ||
    kind === "blockquote" ||
    kind === "media" ||
    kind === "carousel" ||
    kind === "resourceRow"
  ) {
    return null
  }

  return (
    <SubMenuTrigger icon={TypeIcon} label="Font">
      <MenuGroup>
        {POST_EDITOR_FONT_FAMILIES.map((f) => (
          <FontFamilyMenuItem key={f.value} editor={editor} value={f.value} label={f.label} />
        ))}
      </MenuGroup>
    </SubMenuTrigger>
  )
}

const FontSizeMenuItem: React.FC<{ editor: any; value: string; label: string }> = ({ editor, value, label }) => {
  const textStyleAttrs = editor?.getAttributes('textStyle') || {};
  let fontSize = textStyleAttrs.fontSize || '24';
  fontSize = fontSize.toString().replace('px', '');
  const isActive = fontSize === value;
  
  const onClick = React.useCallback(() => {
    if (!editor) return
    editor.chain().focus().setFontSize(`${value}px`).run()
  }, [editor, value])

  return (
    <MenuItem
      render={
        <Button
          data-style="ghost"
          data-active-state={isActive ? "on" : "off"}
        />
      }
      onClick={onClick}
    >
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const FontSizeActionGroup: React.FC<{ editor: any; node: TiptapNode | null }> = ({ editor, node }) => {
  // Node types that should NOT show font size options
  const mediaResourceNodeTypes = ['image', 'video', 'embedVideo', 'downloadRow', 'externalLinkRow', 'meetingRow'];
  const nodeType = node?.type?.name || '';
  const isMediaOrResource = mediaResourceNodeTypes.includes(nodeType);

  // Don't show font size options for media/resource nodes
  if (isMediaOrResource) {
    return null;
  }

  const FONT_SIZES = [
    { value: "16", label: "16px" },
    { value: "20", label: "20px" },
    { value: "24", label: "24px" },
    { value: "28", label: "28px" },
    { value: "32", label: "32px" },
    { value: "36", label: "36px" },
    { value: "40", label: "40px" },
  ]

  return (
    <SubMenuTrigger icon={TypeIcon} label="Size">
      <MenuGroup>
        {FONT_SIZES.map((s) => (
          <FontSizeMenuItem key={s.value} editor={editor} value={s.value} label={s.label} />
        ))}
      </MenuGroup>
    </SubMenuTrigger>
  )
}

// Decorative "01." preview tile used by the marker-color submenu. Mirrors the
// toolbar's `.ps-ol-swatch__marker` look (colored leading-zero digit) so the
// drag-context picker reads as the same control.
const OrderedMarkerIndicator: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden="true"
    className="tiptap-button-icon"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "20px",
      color,
      fontWeight: 700,
      fontSize: "11px",
      letterSpacing: "0.02em",
      flexShrink: 0,
    }}
  >
    01.
  </span>
)

// One row in the orderedList "Numbered list" sub-submenu — flips the active
// list's `markerColor` attribute. Active swatch is highlighted via
// `data-active-state` so the current marker color reads as selected.
const OrderedMarkerMenuItem: React.FC<{
  editor: any
  hex: string
  label: string
  isActive: boolean
}> = ({ editor, hex, label, isActive }) => {
  const onClick = React.useCallback(() => {
    if (!editor) return
    ;(editor as any)
      .chain()
      .focus()
      .updateAttributes("orderedList", { markerColor: hex })
      .run()
  }, [editor, hex])

  return (
    <MenuItem
      render={
        <Button data-style="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={onClick}
    >
      <OrderedMarkerIndicator color={hex} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const FormatActionGroup: React.FC<{ editor: any; node: TiptapNode | null }> = ({ editor, node }) => {
  // Format submenu for:
  // - Body: underline + link + UL/OL
  // - Caption: underline + link
  // - Heading/Subheading: underline only
  if (!editor) return null
  const kind = getNodeMenuKind(node)
  if (kind !== "paragraph" && kind !== "caption" && kind !== "heading" && kind !== "subheading") {
    return null
  }

  const isUnderlineActive = !!editor.isActive("underline")
  const isLinkActive = !!editor.isActive("link")
  const isBulletActive = !!editor.isActive("bulletList")
  const isOrderedActive = !!editor.isActive("orderedList")

  const showLink = kind === "paragraph" || kind === "caption"
  const showLists = kind === "paragraph"

  return (
    <SubMenuTrigger
      icon={TypeIcon}
      label="Format"
      isActive={isUnderlineActive || (showLink && isLinkActive) || (showLists && (isBulletActive || isOrderedActive))}
    >
      <MenuGroup>
        <BaseMenuItem
          icon={TypeIcon}
          label="Underline"
          disabled={!editor}
          isActive={isUnderlineActive}
          onClick={() => (editor as any).chain().focus().toggleMark("underline").run()}
        />

        {showLink && (
          <BaseMenuItem
            icon={LinkIcon}
            label="Link"
            disabled={!editor}
            isActive={isLinkActive}
            onClick={() => {
              if (selectionInRestrictedInlineLinkContext(editor.state)) return
              const previousUrl = editor.getAttributes("link")?.href
              const url = window.prompt("Enter a URL", previousUrl || "https://")
              if (url === null) return
              if (url === "") {
                ;(editor as any).chain().focus().unsetLink().run()
              } else {
                ;(editor as any).chain().focus().setLink({ href: url }).run()
              }
            }}
          />
        )}

        {showLists && (
          <>
            <BaseMenuItem
              icon={ListIcon}
              label={isBulletActive ? "Remove Bullet List" : "Bullet List"}
              disabled={!editor}
              isActive={isBulletActive}
              onClick={() => (editor as any).chain().focus().toggleBulletList().run()}
            />

            <BaseMenuItem
              icon={ListOrderedIcon}
              label={isOrderedActive ? "Remove Numbered List" : "Numbered List"}
              disabled={!editor}
              isActive={isOrderedActive}
              onClick={() => (editor as any).chain().focus().toggleOrderedList().run()}
            />
          </>
        )}
      </MenuGroup>
    </SubMenuTrigger>
  )
}

const TransformActionGroup: React.FC<{ editor: any; node: TiptapNode | null; nodePos?: number }> = ({ editor, node, nodePos }) => {
  // Per the new spec:
  // - Spacer: drag menu is just "Delete" — no Turn Into entry at all.
  // - Blockquote: only Color + Delete — no Turn Into entry.
  // - Carousel: only Edit + Delete — no Turn Into entry (a carousel can't
  //   meaningfully turn into anything else; user must delete and re-insert).
  // The resourceRow / media short-circuits below preserve the existing
  // hide-Turn-Into-for-resource-rows behaviour through the resolved nodeType.
  const kindFromNode = getNodeMenuKind(node)
  if (
    kindFromNode === "spacer" ||
    kindFromNode === "horizontalRule" ||
    kindFromNode === "blockquote" ||
    kindFromNode === "carousel"
  ) {
    return null
  }

  // Node types that should NOT show list conversion options
  const mediaResourceNodeTypes = ['image', 'video', 'embedVideo', 'downloadRow', 'externalLinkRow', 'meetingRow'];
  
  // Try to get node type from the passed node, or from editor selection/position
  let nodeType = node?.type?.name || '';
  const isInitialResourceTable = nodeType === 'resourceTable';
  
  // Always check nodePos and editor selection to find the actual row node
  // (the passed node might be a child like paragraph or parent like resourceTable)
  if (editor) {
    // If initial node is resourceTable, prioritize finding row nodes INSIDE it
    // Don't walk backwards to find previous image/video nodes
    if (isInitialResourceTable) {
      // Strategy 1: Check editor selection to find row node inside resourceTable
      const { selection } = editor.state;
      if (selection && !selection.empty) {
        const { $from } = selection;
        // Walk up the node tree, but prioritize row nodes (downloadRow, externalLinkRow, meetingRow)
        // over media nodes (image, video) when we know we're in a resourceTable
        const rowNodeTypes = ['downloadRow', 'externalLinkRow', 'meetingRow'];
        for (let depth = $from.depth; depth >= 0; depth--) {
          const nodeAtDepth = $from.node(depth);
          const nodeTypeAtDepth = nodeAtDepth.type.name;
          // If we find a row node, use it immediately
          if (rowNodeTypes.includes(nodeTypeAtDepth)) {
            nodeType = nodeTypeAtDepth;
            break;
          }
        }
      }
      
      // Strategy 2: If we have nodePos, try to find row node from resourceTable's children
      if (nodeType === 'resourceTable' && typeof nodePos === 'number' && nodePos >= 0) {
        const tableNode = editor.state.doc.nodeAt(nodePos);
        if (tableNode && tableNode.type.name === 'resourceTable') {
          // Look at the resourceTable's children to find the row type
          // resourceTable contains: (downloadRow | externalLinkRow | meetingRow)+
          if (tableNode.content && tableNode.content.childCount > 0) {
            // Walk through all children to find which row contains the selection
            let foundRowNode: any = null;
            
            if (selection && !selection.empty) {
              const selectionPos = selection.from;
              // Check each child row to see if selection is inside it
              tableNode.content.forEach((child: any, offset: number) => {
                const childStart = nodePos + 1 + offset;
                const childEnd = childStart + child.nodeSize;
                if (selectionPos >= childStart && selectionPos < childEnd) {
                  if (mediaResourceNodeTypes.includes(child.type.name)) {
                    foundRowNode = child;
                  }
                }
              });
            }
            
            // If we didn't find it from selection, use the first row as fallback
            if (!foundRowNode && tableNode.content.firstChild) {
              foundRowNode = tableNode.content.firstChild;
            }
            
            if (foundRowNode && mediaResourceNodeTypes.includes(foundRowNode.type.name)) {
              nodeType = foundRowNode.type.name;
            }
          }
        }
      }
      
      // Strategy 3: Walk forward from nodePos to find child row nodes
      if (nodeType === 'resourceTable' && typeof nodePos === 'number' && nodePos >= 0) {
        // Walk forward from nodePos to find child row nodes
        let pos = nodePos + 1;
        let checked = 0;
        const maxPos = editor.state.doc.content.size - 1;
        while (pos <= maxPos && checked < 50) {
          const checkNode = editor.state.doc.nodeAt(pos);
          if (checkNode && mediaResourceNodeTypes.includes(checkNode.type.name)) {
            nodeType = checkNode.type.name;
            break;
          }
          pos++;
          checked++;
        }
      }
      
      // If still resourceTable, hide the menu to be safe
      if (nodeType === 'resourceTable') {
        return null;
      }
    } else {
      // If initial node is NOT resourceTable, use the original logic
      // First, check if we're dealing with a paragraph - if so, prioritize it
      const isParagraph = node?.type?.name === 'paragraph';
      
      // First, try to get node from nodePos if available
      if (typeof nodePos === 'number' && nodePos >= 0) {
        const nodeAtPos = editor.state.doc.nodeAt(nodePos);
        // If node at pos is a paragraph, use it (don't look for image/video)
        if (nodeAtPos && nodeAtPos.type.name === 'paragraph') {
          nodeType = 'paragraph';
        } else if (nodeAtPos && mediaResourceNodeTypes.includes(nodeAtPos.type.name)) {
          nodeType = nodeAtPos.type.name;
        } else if (!isParagraph) {
          // Only walk backwards if we're not dealing with a paragraph
          // Walk backwards from nodePos to find parent row node
          let pos = nodePos;
          let checked = 0;
          while (pos >= 0 && checked < 50) { // Limit search to prevent infinite loops
            const checkNode = editor.state.doc.nodeAt(pos);
            if (checkNode && mediaResourceNodeTypes.includes(checkNode.type.name)) {
              nodeType = checkNode.type.name;
              break;
            }
            pos--;
            checked++;
          }
        }
      }
      
      // If we still haven't found a row type, check editor selection
      if (!mediaResourceNodeTypes.includes(nodeType) && !isParagraph) {
        const { selection } = editor.state;
        if (selection) {
          // Check NodeSelection first (most reliable)
          if (selection instanceof NodeSelection) {
            const selectedNode = selection.node;
            if (selectedNode && mediaResourceNodeTypes.includes(selectedNode.type.name)) {
              nodeType = selectedNode.type.name;
            }
          }
          
          // Also check $from to walk up the node tree
          if (!mediaResourceNodeTypes.includes(nodeType) && !selection.empty) {
            const { $from } = selection;
            // Check if we're in a paragraph first - if so, use paragraph
            const currentNode = $from.node($from.depth);
            if (currentNode.type.name === 'paragraph') {
              nodeType = 'paragraph';
            } else {
              // Walk up the node tree to find a row node
              for (let depth = $from.depth; depth >= 0; depth--) {
                const nodeAtDepth = $from.node(depth);
                const nodeTypeAtDepth = nodeAtDepth.type.name;
                if (mediaResourceNodeTypes.includes(nodeTypeAtDepth)) {
                  nodeType = nodeTypeAtDepth;
                  break;
                }
              }
            }
          }
        }
      }
      
      // If we still don't have a nodeType and the initial node is a paragraph, use it
      if (!nodeType && isParagraph) {
        nodeType = 'paragraph';
      }
    }
  }
  
  const isMediaOrResource = mediaResourceNodeTypes.includes(nodeType);
  
  // Determine which options to show based on node type
  const isDownloadRow = nodeType === 'downloadRow';
  const isExternalLinkRow = nodeType === 'externalLinkRow';
  const isMeetingRow = nodeType === 'meetingRow';
  const isVideo = nodeType === 'video' || nodeType === 'embedVideo';
  const isImage = nodeType === 'image';
  
  // For downloadRow, externalLinkRow, meetingRow: don't show Turn Into menu at all
  if (isDownloadRow || isExternalLinkRow || isMeetingRow) {
    return null;
  }
  
  // For paragraphs that already carry a `postTextStyle` of "heading" /
  // "subheading", the spec calls for a tightly-scoped Turn Into menu that
  // only offers the *other* paragraph styles (no Underline/Link/list/quote
  // entries). Short-circuit before any item-building so the rest of the
  // function only deals with the legacy "everything else" path.
  if (kindFromNode === "heading" || kindFromNode === "subheading" || kindFromNode === "caption") {
    const TARGET_STYLES: Array<{ style: "heading" | "subheading" | "body" | "caption"; label: string }> =
      kindFromNode === "heading"
        ? [
            { style: "subheading", label: "Subheading" },
            { style: "body", label: "Body" },
            { style: "caption", label: "Caption" },
          ]
        : kindFromNode === "subheading"
        ? [
            { style: "heading", label: "Heading" },
            { style: "body", label: "Body" },
            { style: "caption", label: "Caption" },
          ]
        : [
            { style: "body", label: "Body" },
            { style: "heading", label: "Heading" },
            { style: "subheading", label: "Subheading" },
          ]

    return (
      <SubMenuTrigger icon={Repeat2Icon} label="Turn Into">
        <MenuGroup>
          {TARGET_STYLES.map((target) => (
            <BaseMenuItem
              key={target.style}
              icon={TypeIcon}
              label={target.label}
              disabled={!editor}
              isActive={false}
              onClick={() => {
                if (!editor) return
                ;(editor as any)
                  .chain()
                  .focus()
                  .setPostParagraphStyle({ style: target.style })
                  .run()
              }}
            />
          ))}
        </MenuGroup>
      </SubMenuTrigger>
    )
  }

  // Build items array conditionally based on node type
  const items: Array<{
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    disabled: boolean;
    isActive: boolean;
  }> = [];

  // For video and image: underline and link
  if (isVideo || isImage) {
    items.push({
      icon: TypeIcon,
      label: "Underline",
      onClick: () => editor && (editor as any).chain().focus().toggleMark("underline").run(),
      disabled: !editor,
      isActive: !!editor?.isActive("underline"),
    });
    items.push({
      icon: LinkIcon,
      label: "Link",
      onClick: () => {
        if (!editor) return
        const imageAttrs = editor.getAttributes("image") || {}
        if (imageAttrs.src) {
          const previousUrl = imageAttrs.href
          const url = window.prompt("Enter a URL", previousUrl || "https://")
          if (url === null) return
          if (url === "") {
            editor.chain().focus().updateAttributes("image", { href: null }).run()
          } else {
            editor.chain().focus().updateAttributes("image", { href: url }).run()
          }
          return
        }
        if (selectionInRestrictedInlineLinkContext(editor.state)) return
        const previousUrl = editor.getAttributes("link")?.href
        const url = window.prompt("Enter a URL", previousUrl || "https://")
        if (url === null) return
        if (url === "") {
          ;(editor as any).chain().focus().unsetLink().run()
        } else {
          ;(editor as any).chain().focus().setLink({ href: url }).run()
        }
      },
      disabled: !editor,
      isActive: !!editor?.isActive("link"),
    });
  }
  // For other nodes: show all options
  else {
    // Body/unstyled paragraphs: underline/link live under the dedicated "Format" submenu.
    if (kindFromNode !== "paragraph") {
      items.push({
        icon: TypeIcon,
        label: "Underline",
        onClick: () => editor && (editor as any).chain().focus().toggleMark("underline").run(),
        disabled: !editor,
        isActive: !!editor?.isActive("underline"),
      });
      // Inline text links only for list blocks (heading/subheading use the short-circuit path above).
      if (kindFromNode === "bulletList" || kindFromNode === "orderedList") {
        items.push({
          icon: LinkIcon,
          label: "Link",
          onClick: () => {
            if (!editor) return
            if (selectionInRestrictedInlineLinkContext(editor.state)) return
            const previousUrl = editor.getAttributes("link")?.href
            const url = window.prompt("Enter a URL", previousUrl || "https://")
            if (url === null) return
            if (url === "") {
              ;(editor as any).chain().focus().unsetLink().run()
            } else {
              ;(editor as any).chain().focus().setLink({ href: url }).run()
            }
          },
          disabled: !editor,
          isActive: !!editor?.isActive("link"),
        });
      }
    }

    // Body/unstyled paragraphs should be able to turn into Heading/Subheading/Caption.
    // (Heading/Subheading/Caption themselves take the special short-circuit path above.)
    if (kindFromNode === "paragraph") {
      items.push({
        icon: TypeIcon,
        label: "Heading",
        onClick: () => editor && (editor as any).chain().focus().setPostParagraphStyle({ style: "heading" }).run(),
        disabled: !editor,
        isActive: false,
      });
      items.push({
        icon: TypeIcon,
        label: "Subheading",
        onClick: () => editor && (editor as any).chain().focus().setPostParagraphStyle({ style: "subheading" }).run(),
        disabled: !editor,
        isActive: false,
      });
      items.push({
        icon: TypeIcon,
        label: "Caption",
        onClick: () => editor && (editor as any).chain().focus().setPostParagraphStyle({ style: "caption" }).run(),
        disabled: !editor,
        isActive: false,
      });
    }

    // List conversion options
    // Custom handler that preserves nested nodes (like downloadRow) when converting between list types
    // Body/unstyled paragraphs: list toggles live under the dedicated "Format" submenu.
    if (kindFromNode !== "paragraph") items.push({
      icon: ListIcon,
      label: editor?.isActive("bulletList") ? "Remove Bullet List" : "Bullet List",
      onClick: () => {
        if (!editor) return;
        const { selection, doc } = editor.state;
        const { $from } = selection;
        
        // Try to find the list node - check node parameter first, then nodePos, then selection
        let listPos = -1;
        let listNode = null;
        
        // First, check if the node itself is a list
        if (node && (node.type.name === 'bulletList' || node.type.name === 'orderedList')) {
          listNode = node;
          // Try to find the position
          if (typeof nodePos === 'number' && nodePos >= 0) {
            listPos = nodePos;
          } else {
            // Find position by searching the document
            doc.descendants((n, pos) => {
              if (n === node) {
                listPos = pos;
                return false; // stop iteration
              }
            });
          }
        }
        
        // If not found, try using nodePos if available
        if (!listNode && typeof nodePos === 'number' && nodePos >= 0) {
          const nodeAtPos = doc.nodeAt(nodePos);
          if (nodeAtPos && (nodeAtPos.type.name === 'bulletList' || nodeAtPos.type.name === 'orderedList')) {
            listPos = nodePos;
            listNode = nodeAtPos;
          }
        }
        
        // If still not found, use selection-based approach
        if (!listNode) {
          for (let depth = $from.depth; depth >= 0; depth--) {
            const nodeAtDepth = $from.node(depth);
            if (nodeAtDepth.type.name === 'bulletList' || nodeAtDepth.type.name === 'orderedList') {
              listPos = $from.start(depth);
              listNode = nodeAtDepth;
              break;
            }
          }
        }
        
        if (listNode && listPos >= 0) {
          // We're in a list - convert the list type while preserving all content
          if (listNode.type.name === 'bulletList') {
            // Already bullet list, remove it by unwrapping while preserving content
            editor.chain().focus().command(({ tr, dispatch }) => {
              if (!dispatch) return false;
              try {
                const listEnd = listPos + listNode.nodeSize;
                // Extract all list items and their content
                const unwrappedContent = [];
                listNode.content.forEach((listItem) => {
                  // Add each list item's content directly (this preserves nested nodes)
                  listItem.content.forEach((itemContent) => {
                    unwrappedContent.push(itemContent);
                  });
                });
                // Replace the list with its unwrapped content
                tr.replaceWith(listPos, listEnd, unwrappedContent);
                return true;
              } catch (error) {
                console.error('Error removing bullet list:', error);
                return false;
              }
            }).run();
          } else {
            // Convert orderedList to bulletList by replacing the entire list
            // Use command to directly manipulate the transaction
            editor.chain().focus().command(({ tr, dispatch }) => {
              if (!dispatch) return false;
              try {
                const listEnd = listPos + listNode.nodeSize;
                // Create new bulletList with the exact same content Fragment
                // This preserves all nested nodes including downloadRow
                const newList = editor.schema.nodes.bulletList.create(
                  listNode.attrs,
                  listNode.content
                );
                tr.replaceWith(listPos, listEnd, newList);
                return true;
              } catch (error) {
                console.error('Error converting list type:', error);
                return false;
              }
            }).run();
          }
        } else {
          // Not in a list, use standard toggle
          editor.chain().focus().toggleBulletList().run();
        }
      },
      disabled: !editor,
      isActive: !!editor?.isActive("bulletList"),
    });
    if (kindFromNode !== "paragraph") items.push({
      icon: ListOrderedIcon,
      label: editor?.isActive("orderedList") ? "Remove Numbered List" : "Numbered List",
      onClick: () => {
        if (!editor) return;
        const { selection, doc } = editor.state;
        const { $from } = selection;
        
        // Try to find the list node - check node parameter first, then nodePos, then selection
        let listPos = -1;
        let listNode = null;
        
        // First, check if the node itself is a list
        if (node && (node.type.name === 'bulletList' || node.type.name === 'orderedList')) {
          listNode = node;
          // Try to find the position
          if (typeof nodePos === 'number' && nodePos >= 0) {
            listPos = nodePos;
          } else {
            // Find position by searching the document
            doc.descendants((n, pos) => {
              if (n === node) {
                listPos = pos;
                return false; // stop iteration
              }
            });
          }
        }
        
        // If not found, try using nodePos if available
        if (!listNode && typeof nodePos === 'number' && nodePos >= 0) {
          const nodeAtPos = doc.nodeAt(nodePos);
          if (nodeAtPos && (nodeAtPos.type.name === 'bulletList' || nodeAtPos.type.name === 'orderedList')) {
            listPos = nodePos;
            listNode = nodeAtPos;
          }
        }
        
        // If still not found, use selection-based approach
        if (!listNode) {
          for (let depth = $from.depth; depth >= 0; depth--) {
            const nodeAtDepth = $from.node(depth);
            if (nodeAtDepth.type.name === 'bulletList' || nodeAtDepth.type.name === 'orderedList') {
              listPos = $from.start(depth);
              listNode = nodeAtDepth;
              break;
            }
          }
        }
        
        if (listNode && listPos >= 0) {
          // We're in a list - convert the list type while preserving all content
          if (listNode.type.name === 'orderedList') {
            // Already ordered list, remove it by unwrapping while preserving content
            editor.chain().focus().command(({ tr, dispatch }) => {
              if (!dispatch) return false;
              try {
                const listEnd = listPos + listNode.nodeSize;
                // Extract all list items and their content
                const unwrappedContent = [];
                listNode.content.forEach((listItem) => {
                  // Add each list item's content directly (this preserves nested nodes)
                  listItem.content.forEach((itemContent) => {
                    unwrappedContent.push(itemContent);
                  });
                });
                // Replace the list with its unwrapped content
                tr.replaceWith(listPos, listEnd, unwrappedContent);
                return true;
              } catch (error) {
                console.error('Error removing ordered list:', error);
                return false;
              }
            }).run();
          } else {
            // Convert bulletList to orderedList by replacing the entire list
            // Use command to directly manipulate the transaction
            editor.chain().focus().command(({ tr, dispatch }) => {
              if (!dispatch) return false;
              try {
                const listEnd = listPos + listNode.nodeSize;
                // Create new orderedList with the exact same content Fragment
                // This preserves all nested nodes including downloadRow
                const newList = editor.schema.nodes.orderedList.create(
                  listNode.attrs,
                  listNode.content
                );
                tr.replaceWith(listPos, listEnd, newList);
                return true;
              } catch (error) {
                console.error('Error converting list type:', error);
                return false;
              }
            }).run();
          }
        } else {
          // Not in a list, use standard toggle
          editor.chain().focus().toggleOrderedList().run();
        }
      },
      disabled: !editor,
      isActive: !!editor?.isActive("orderedList"),
    });
    // Per the new spec, Blockquote is no longer offered in the Turn Into
    // submenu — the toolbar's blockquote button + the per-blockquote color
    // popover already cover insert/remove and styling.
  }

  // Don't show the "Turn Into" menu if there are no items
  if (items.length === 0) {
    return null;
  }

  const formatLabels = new Set([
    "Underline",
    "Link",
    "Bullet List",
    "Remove Bullet List",
    "Numbered List",
    "Remove Numbered List",
  ])
  const turnIntoLabel = items.some((it) => formatLabels.has(it.label)) ? "Format" : "Turn Into"

  // When the active block is an orderedList, the "Numbered List" entry inside
  // Turn Into morphs into a sub-submenu: it shows the same marker-color
  // options as the toolbar's numbered-list popover, plus a "Remove Numbered
  // List" action. Outside an orderedList the entry stays a plain toggle.
  const isOrderedListActive = !!editor?.isActive("orderedList")
  const numberedListLabel = "Numbered List"
  const numberedListItem = items.find((it) => {
    const lbl = it.label
    return lbl === numberedListLabel || lbl === "Remove Numbered List"
  })
  const removeNumberedList = numberedListItem?.onClick
  const currentMarkerColor =
    (((editor?.getAttributes?.("orderedList") as any)?.markerColor) || "#000000")
      .toString()
      .toUpperCase()

  return (
    <SubMenuTrigger icon={Repeat2Icon} label={turnIntoLabel}>
      <MenuGroup>
        {items.map((item) => {
          if (
            isOrderedListActive &&
            (item.label === numberedListLabel || item.label === "Remove Numbered List")
          ) {
            return (
              <SubMenuTrigger
                key="numbered-list-submenu"
                icon={ListOrderedIcon}
                label="Numbered List"
                isActive={true}
              >
                <MenuGroup>
                  {ORDERED_MARKER_OPTIONS.map((opt) => (
                    <OrderedMarkerMenuItem
                      key={opt.hex}
                      editor={editor}
                      hex={opt.hex}
                      label={opt.label}
                      isActive={opt.hex.toUpperCase() === currentMarkerColor}
                    />
                  ))}
                  <BaseMenuItem
                    icon={ListOrderedIcon}
                    label="Remove Numbered List"
                    disabled={!editor || !removeNumberedList}
                    isActive={false}
                    onClick={() => {
                      if (removeNumberedList) removeNumberedList()
                    }}
                  />
                </MenuGroup>
              </SubMenuTrigger>
            )
          }
          return <BaseMenuItem key={item.label} {...item} />
        })}
      </MenuGroup>
    </SubMenuTrigger>
  )
}

const ResetFormattingAction: React.FC = () => {
  const { canReset, handleResetFormatting, label, Icon } =
    useResetAllFormatting({
      hideWhenUnavailable: true,
      preserveMarks: ["inlineThread"],
    })

  return (
    <BaseMenuItem
      icon={Icon}
      label={label}
      disabled={!canReset}
      onClick={handleResetFormatting}
    />
  )
}

const ImageActionGroup: React.FC = () => {
  const { canDownload, handleDownload, label, Icon } = useImageDownload({
    hideWhenUnavailable: true,
  })

  return (
    <>
      <BaseMenuItem
        icon={Icon}
        label={label}
        disabled={!canDownload}
        onClick={handleDownload}
      />

      <Separator orientation="horizontal" />
    </>
  )
}

const MediaActionGroup: React.FC<{ editor: any; node: TiptapNode | null; nodePos: number; onClose: () => void }> = ({ editor, node, nodePos, onClose }) => {
  const { handleDownload, canDownload, Icon: DownloadIcon, label: downloadLabel } = useImageDownload({ editor, hideWhenUnavailable: true })
  const { handleToggleCaption, canToggle, Icon: CaptionIcon, label: captionLabel } = useImageCaption({ editor, hideWhenUnavailable: true })
  const { handleImage: handleReplace, canInsert, Icon: UploadIcon, label: uploadLabel } = useImageUpload({ editor, hideWhenUnavailable: true })
  const isEmbedVideo = node?.type?.name === 'embedVideo'

  const handleAction = React.useCallback(async (action: () => any) => {
    // Ensure node is selected and floating toolbar hidden before action
    try {
      selectNodeAndHideFloating(editor, nodePos)
      onClose()
    } catch {}
    // Give selection a tick to settle
    setTimeout(() => {
      try {
        action()
      } catch (e) {
        // swallow
      }
    }, 10)
  }, [editor, nodePos, onClose])

  const handleReplaceMedia = React.useCallback(() => {
    if (!editor || !node) return;
    
    try {
      selectNodeAndHideFloating(editor, nodePos)
      onClose()
    } catch {}
    
    // Dispatch custom event to open ResourceModal for replacement
    setTimeout(() => {
      try {
        const nodeType = node.type?.name || '';
        const nodeDOM = editor.view.nodeDOM(nodePos);
        
        if (nodeDOM) {
          const eventName = nodeType === 'video' 
            ? 'tiptap:video:requestReplace' 
            : 'tiptap:image:requestReplace';
          
          const ev = new CustomEvent(eventName, {
            bubbles: true,
            cancelable: true,
            detail: {
              nodePos: nodePos,
              resourceId: node.attrs?.resourceId || '',
              src: node.attrs?.src || '',
            }
          });
          
          nodeDOM.dispatchEvent(ev);
          // Also try dispatching on document as fallback
          if (!ev.defaultPrevented) {
            document.dispatchEvent(ev);
          }
        }
      } catch (e) {
        console.error('Failed to dispatch media replacement event:', e);
      }
    }, 10);
  }, [editor, node, nodePos, onClose]);

  return (
    <MenuGroup>
      <BaseMenuItem
        icon={CaptionIcon}
        label={captionLabel}
        disabled={!canToggle}
        onClick={() => handleAction(() => handleToggleCaption())}
      />

      {!isEmbedVideo && (
        <BaseMenuItem
          icon={UploadIcon}
          label='Replace Image/Video'
          disabled={!canInsert}
          onClick={handleReplaceMedia}
        />
      )}

      {!isEmbedVideo && (
        <BaseMenuItem
          icon={DownloadIcon}
          label={downloadLabel}
          disabled={!canDownload}
          onClick={() => handleAction(() => handleDownload())}
        />
      )}

      <Separator orientation="horizontal" />
    </MenuGroup>
  )
}

// Remove duplicate/copy core actions entirely

// Remove AI group entirely

// Carousel drag-context actions. Per spec the drag menu only exposes
// "Edit carousel" + "Delete" for postImageCarousel nodes; Color/Font/Turn
// Into/Media submenus are all hidden upstream. Edit dispatches a custom
// event that the toolbar listens for and opens ResourceModal in
// edit-carousel mode (pre-selected slides). Delete keeps its default
// rendering via DeleteActionGroup at the bottom of the menu.
const CarouselActionGroup: React.FC<{ editor: any; node: TiptapNode | null; nodePos: number; onClose: () => void }> = ({
  editor,
  node,
  nodePos,
  onClose,
}) => {
  const handleEdit = React.useCallback(() => {
    if (!editor || !node || node.type?.name !== "postImageCarousel") return
    try {
      selectNodeAndHideFloating(editor, nodePos)
    } catch {}
    try {
      onClose()
    } catch {}
    setTimeout(() => {
      try {
        const slides = (node.attrs as any)?.slides
        const ev = new CustomEvent("tiptap:carousel:requestEdit", {
          bubbles: true,
          cancelable: true,
          detail: { nodePos, slides: Array.isArray(slides) ? slides : [] },
        })
        document.dispatchEvent(ev)
      } catch (e) {
        console.error("Failed to dispatch carousel edit event:", e)
      }
    }, 10)
  }, [editor, node, nodePos, onClose])

  return (
    <MenuGroup>
      <BaseMenuItem icon={PencilIcon} label="Edit carousel" onClick={handleEdit} />
      <Separator orientation="horizontal" />
    </MenuGroup>
  )
}

const DeleteActionGroup: React.FC<{ editor: any }> = ({ editor }) => {
  const { handleDeleteNode, canDeleteNode, label, Icon } = useDeleteNode({ editor })

  return (
    <MenuGroup>
      <BaseMenuItem
        icon={Icon}
        label={label}
        onClick={handleDeleteNode}
        disabled={!canDeleteNode}
      />
    </MenuGroup>
  )
}

export const DragContextMenu: React.FC<DragContextMenuProps> = ({
  editor: providedEditor,
  withSlashCommandTrigger = true,
  mobileBreakpoint = 768,
  ...props
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { aiGenerationActive, isDragging } = useUiEditorState(editor)
  const isMobile = useIsMobile(mobileBreakpoint)
  const [open, setOpen] = React.useState(false)
  const [node, setNode] = React.useState<TiptapNode | null>(null)
  const [nodePos, setNodePos] = React.useState<number>(-1)

  const handleNodeChange = React.useCallback((data: NodeChangeData) => {
    if (data.node) setNode(data.node)
    setNodePos(data.pos)
  }, [])

  React.useEffect(() => {
    if (!editor) return
    ;(editor as any).commands.setLockDragHandle?.(open)
    editor.commands.setMeta("lockDragHandle", open)
  }, [editor, open])

  // We limit menu to Color, Turn Into, and Delete only

  const dynamicPositions = React.useMemo(() => {
    return {
      middleware: [
        offset((props) => {
          const { rects } = props
          const nodeHeight = rects.reference.height
          const dragHandleHeight = rects.floating.height

          const crossAxis = nodeHeight / 2 - dragHandleHeight / 2

          return {
            mainAxis: 16,
            // if height is more than 40px, then it's likely a block node
            crossAxis: nodeHeight > 40 ? 0 : crossAxis,
          }
        }),
      ],
    }
  }, [])

  const handleOnMenuClose = React.useCallback(() => {
    if (editor) {
      editor.commands.setMeta("hideDragHandle", true)
    }
  }, [editor])

  const onElementDragStart = React.useCallback(() => {
    if (!editor) return
    ;(editor as any).commands.setIsDragging?.(true)
  }, [editor])

  const onElementDragEnd = React.useCallback(() => {
    if (!editor) return
    ;(editor as any).commands.setIsDragging?.(false)
  }, [editor])

  // When drag handle is on a listItem that contains a single image/video/embedVideo, show media actions for that child
  const { menuNode, menuNodePos } = React.useMemo(() => {
    let resolvedNode: TiptapNode | null = node;
    let resolvedPos = nodePos;
    if (node?.type?.name === 'listItem' && node.content && typeof nodePos === 'number' && nodePos >= 0) {
      let contentOffset = 0;
      for (let i = 0; i < node.content.childCount; i++) {
        const child = node.content.child(i);
        if (child.type.name === 'image' || child.type.name === 'video' || child.type.name === 'embedVideo') {
          resolvedNode = child;
          resolvedPos = nodePos + 1 + contentOffset;
          break;
        }
        contentOffset += child.nodeSize;
      }
    }
    return { menuNode: resolvedNode, menuNodePos: resolvedPos };
  }, [node, nodePos])

  if (!editor) return null

  return (
    <DragHandleAny
      editor={editor}
      onNodeChange={handleNodeChange}
      computePositionConfig={dynamicPositions}
      onElementDragStart={onElementDragStart}
      onElementDragEnd={onElementDragEnd}
      {...props}
    >
      <ButtonGroup
        orientation="horizontal"
        style={{
          ...(aiGenerationActive || isMobile || isTextSelectionValid(editor)
            ? { opacity: 0, pointerEvents: "none" }
            : {}),
          ...(isDragging ? { opacity: 0 } : {}),
        }}
      >
        {withSlashCommandTrigger && (
          <SlashCommandTriggerButton
            node={node}
            nodePos={nodePos}
            data-weight="small"
          />
        )}

        <Menu
          open={open}
          onOpenChange={setOpen}
          placement="left"
          trigger={
            <MenuButton
              render={
                <Button
                  data-style="ghost"
                  tabIndex={-1}
                  tooltip={
                    <>
                      <div>Click for options</div>
                      <div>Hold for drag</div>
                    </>
                  }
                  data-weight="small"
                  style={{
                    cursor: "grab",
                    ...(open ? { pointerEvents: "none" } : {}),
                  }}
                  onMouseDown={() => selectNodeAndHideFloating(editor, nodePos)}
                >
                  <GripVerticalIcon className="tiptap-button-icon" />
                </Button>
              }
            />
          }
        >
          <MenuContent
            onClose={handleOnMenuClose}
            autoFocusOnHide={false}
            preventBodyScroll={true}
            portal
          >
            <Combobox style={SR_ONLY} />
            <ComboboxList style={{ minWidth: "15rem" }}>
              <MenuGroup>
                <ColorActionGroup editor={editor} node={node} />
                <BlockquoteAttributionActionGroup editor={editor} node={node} />
                <ResourceRowSubtitleActionGroup editor={editor} node={node} />
                <HorizontalRuleActionGroup editor={editor} node={node} nodePos={nodePos} />
                <FormatActionGroup editor={editor} node={node} />
                <FontFamilyActionGroup editor={editor} node={node} />
                {/* Size submenu removed per spec — Turn Into + per-type Color
                    cover the styling axes the editor still surfaces here. */}
                <TransformActionGroup editor={editor} node={node} nodePos={nodePos} />
                {(menuNode && (menuNode.type?.name === "image" || menuNode.type?.name === "video" || menuNode.type?.name === "embedVideo")) && (
                  <MediaActionGroup editor={editor} node={menuNode} nodePos={menuNodePos} onClose={() => setOpen(false)} />
                )}

                {/* Carousel-only actions (Edit). Delete is rendered below by
                    DeleteActionGroup. We dispatch a custom event the toolbar
                    listens for, so the modal logic stays colocated with the
                    rest of ResourceModal wiring. */}
                {menuNode?.type?.name === "postImageCarousel" && (
                  <CarouselActionGroup
                    editor={editor}
                    node={menuNode}
                    nodePos={menuNodePos}
                    onClose={() => setOpen(false)}
                  />
                )}

                {/* Upload File for downloadRow, Replace URL for externalLinkRow, Edit URL for meetingRow */}
                {/* Only show row-specific options if the node is NOT an image or video */}
                {/* Only show row-specific options when the drag-handle node is actually a row, or a table/listItem containing the selection in a row */}
                {nodePos >= 0 && node && node.type?.name !== "image" && node.type?.name !== "video" && node.type?.name !== "embedVideo" && (() => {
                  if (!editor || typeof nodePos !== 'number') return null;
                  const ROW_TYPES = ['downloadRow', 'externalLinkRow', 'meetingRow', 'audioRow'];
                  let rowNode: TiptapNode | null = null;
                  let rowPos: number | null = null;
                  const candidate = editor.state.doc.nodeAt(nodePos);
                  const { selection } = editor.state;

                  // Helper: find the row that contains selection inside a table; or if table has exactly one row, use it (for "independent" single-row tables).
                  const findRowInTable = (tablePos: number, tableNode: TiptapNode): { rowNode: TiptapNode; rowPos: number } | null => {
                    if (!tableNode.content || tableNode.content.childCount === 0) return null;
                    const singleRow = tableNode.content.childCount === 1 && ROW_TYPES.includes(tableNode.content.firstChild!.type.name);
                    if (singleRow) {
                      const child = tableNode.content.firstChild!;
                      return { rowNode: child, rowPos: tablePos + 1 };
                    }
                    if (selection.empty) return null;
                    const selFrom = selection.from;
                    let contentOffset = 0;
                    for (let i = 0; i < tableNode.content.childCount; i++) {
                      const child = tableNode.content.child(i);
                      const rowStart = tablePos + 1 + contentOffset;
                      const rowEnd = rowStart + child.nodeSize;
                      if (ROW_TYPES.includes(child.type.name) && selFrom >= rowStart && selFrom < rowEnd) {
                        return { rowNode: child, rowPos: rowStart };
                      }
                      contentOffset += child.nodeSize;
                    }
                    return null;
                  };

                  // Case 1: Drag handle is directly on a row node (works for rows inside or outside lists)
                  if (
                    candidate &&
                    ROW_TYPES.includes(candidate.type.name)
                  ) {
                    rowNode = candidate;
                    rowPos = nodePos;
                  }
                  // Case 2: Drag handle is on the resourceTable (independent single-row or multi-row: use single row or selection)
                  else if (candidate && candidate.type.name === 'resourceTable') {
                    const resolved = findRowInTable(nodePos, candidate);
                    if (resolved) {
                      rowNode = resolved.rowNode;
                      rowPos = resolved.rowPos;
                    }
                  }
                  // Case 3: Drag handle is on a listItem that contains a resourceTable (single row or selection in row)
                  else if (candidate && candidate.type.name === 'listItem' && candidate.content) {
                    let contentOffset = 0;
                    for (let i = 0; i < candidate.content.childCount; i++) {
                      const child = candidate.content.child(i);
                      if (child.type.name === 'resourceTable') {
                        const tablePos = nodePos + 1 + contentOffset;
                        const resolved = findRowInTable(tablePos, child);
                        if (resolved) {
                          rowNode = resolved.rowNode;
                          rowPos = resolved.rowPos;
                        }
                        break;
                      }
                      contentOffset += child.nodeSize;
                    }
                  }

                  if (!rowNode || rowPos === null) return null;

                  const subtitleMenu = (
                    <ResourceRowSubtitleActionGroup
                      editor={editor}
                      node={node}
                      rowNode={rowNode}
                      rowPos={rowPos}
                    />
                  );
                  
                  // Handle downloadRow - trigger file upload
                  if (rowNode.type.name === 'downloadRow') {
                    return (
                      <>
                      {subtitleMenu}
                      <BaseMenuItem
                        icon={RefreshCcwIcon}
                        label="Replace File"
                        onClick={() => {
                          // Find the DOM element for this downloadRow position
                          try {
                            const domAtPos = editor.view.domAtPos(rowPos);
                            let tr: Element | null = null;
                            
                            // Check if we got the tr directly
                            if (domAtPos.node.nodeType === Node.ELEMENT_NODE) {
                              const element = domAtPos.node as Element;
                              if (element.tagName === 'TR' && element.hasAttribute('data-resource-row')) {
                                tr = element;
                              } else {
                                tr = element.closest('tr[data-resource-row]');
                                // If closest didn't find it, try looking in children (for tbody case)
                                if (!tr) {
                                  tr = element.querySelector('tr[data-resource-row]');
                                }
                              }
                            } else if (domAtPos.node.parentElement) {
                              tr = domAtPos.node.parentElement.closest('tr[data-resource-row]');
                              // If closest didn't find it, try looking up and then down
                              if (!tr && domAtPos.node.parentElement.parentElement) {
                                tr = domAtPos.node.parentElement.parentElement.querySelector('tr[data-resource-row]');
                              }
                            }

                            if (tr) {
                              // Dispatch the event from the tr element, just like the double-click handler
                              const ev = new CustomEvent('tiptap:downloadRow:requestReplace', {
                                bubbles: true,
                                detail: {
                                  href: `${rowNode.attrs.href || ''}`,
                                  filename: `${rowNode.attrs.filename || ''}`,
                                  contentType: `${rowNode.attrs.contentType || ''}`,
                                  signedId: `${rowNode.attrs.signedId || ''}`
                                }
                              });
                              tr.dispatchEvent(ev);
                            }
                          } catch (err) {
                            console.error('Failed to trigger file upload for downloadRow:', err);
                          }
                          setOpen(false);
                        }}
                        disabled={!editor}
                      />
                      </>
                    );
                  }
                  
                  // Handle audioRow - open ResourceModal to replace (same as double-click)
                  if (rowNode.type.name === 'audioRow') {
                    return (
                      <>
                      {subtitleMenu}
                      <BaseMenuItem
                        icon={RefreshCcwIcon}
                        label="Replace File"
                        onClick={() => {
                          try {
                            const domAtPos = editor.view.domAtPos(rowPos!);
                            let tr: Element | null = null;
                            if (domAtPos.node.nodeType === Node.ELEMENT_NODE) {
                              const element = domAtPos.node as Element;
                              if (element.tagName === 'TR' && element.hasAttribute('data-resource-row')) {
                                tr = element;
                              } else {
                                tr = element.closest('tr[data-resource-row]') || element.querySelector('tr[data-resource-row]');
                              }
                            } else if (domAtPos.node.parentElement) {
                              tr = domAtPos.node.parentElement.closest('tr[data-resource-row]');
                              if (!tr && domAtPos.node.parentElement.parentElement) {
                                tr = domAtPos.node.parentElement.parentElement.querySelector('tr[data-resource-row]');
                              }
                            }
                            if (tr) {
                              const ev = new CustomEvent('tiptap:audioRow:requestReplace', {
                                bubbles: true,
                                detail: {
                                  resourceId: `${rowNode.attrs.resourceId || ''}`,
                                  href: `${rowNode.attrs.href || ''}`,
                                  filename: `${rowNode.attrs.filename || ''}`,
                                  contentType: `${rowNode.attrs.contentType || ''}`,
                                  signedId: `${rowNode.attrs.signedId || ''}`
                                }
                              });
                              tr.dispatchEvent(ev);
                            }
                          } catch (err) {
                            console.error('Failed to trigger replace for audioRow:', err);
                          }
                          setOpen(false);
                        }}
                        disabled={!editor}
                      />
                      </>
                    );
                  }
                  
                  // Handle externalLinkRow - open ResourceModal (same as double-click)
                  if (rowNode.type.name === 'externalLinkRow') {
                    return (
                      <>
                      {subtitleMenu}
                      <BaseMenuItem
                        icon={LinkIcon}
                        label="Replace URL"
                        onClick={() => {
                          // Find the DOM element for this externalLinkRow position
                          try {
                            const domAtPos = editor.view.domAtPos(rowPos);
                            let tr: Element | null = null;
                            
                            // Check if we got the tr directly
                            if (domAtPos.node.nodeType === Node.ELEMENT_NODE) {
                              const element = domAtPos.node as Element;
                              if (element.tagName === 'TR' && element.hasAttribute('data-resource-row')) {
                                tr = element;
                              } else {
                                tr = element.closest('tr[data-resource-row]');
                                // If closest didn't find it, try looking in children (for tbody case)
                                if (!tr) {
                                  tr = element.querySelector('tr[data-resource-row]');
                                }
                              }
                            } else if (domAtPos.node.parentElement) {
                              tr = domAtPos.node.parentElement.closest('tr[data-resource-row]');
                              // If closest didn't find it, try looking up and then down
                              if (!tr && domAtPos.node.parentElement.parentElement) {
                                tr = domAtPos.node.parentElement.parentElement.querySelector('tr[data-resource-row]');
                              }
                            }

                            if (tr) {
                              // Dispatch the event from the tr element, just like the double-click handler
                              const ev = new CustomEvent('tiptap:externalLinkRow:requestReplace', {
                                bubbles: true,
                                cancelable: true,
                                detail: {
                                  resourceId: `${rowNode.attrs.resourceId || ''}`,
                                  href: `${rowNode.attrs.href || ''}`
                                }
                              });
                              const dispatched = tr.dispatchEvent(ev);
                              // Also try dispatching on document as fallback
                              if (!dispatched) {
                                document.dispatchEvent(ev);
                              }
                            } else {
                              console.warn('Could not find <tr> element for externalLinkRow at position', rowPos, 'domAtPos:', domAtPos);
                              // Try dispatching on document as fallback
                              const ev = new CustomEvent('tiptap:externalLinkRow:requestReplace', {
                                bubbles: true,
                                cancelable: true,
                                detail: {
                                  resourceId: `${rowNode.attrs.resourceId || ''}`,
                                  href: `${rowNode.attrs.href || ''}`
                                }
                              });
                              // Create a synthetic target element for the event
                              Object.defineProperty(ev, 'target', { value: null, writable: false });
                              document.dispatchEvent(ev);
                            }
                          } catch (err) {
                            console.error('Failed to trigger ResourceModal for externalLinkRow:', err);
                          }
                          setOpen(false);
                        }}
                        disabled={!editor}
                      />
                      </>
                    );
                  }
                  
                  // Handle meetingRow - edit URL (keep prompt for now)
                  return (
                    <>
                      {subtitleMenu}
                      <BaseMenuItem
                        icon={LinkIcon}
                        label="Edit URL"
                        onClick={() => {
                          const prevUrl = rowNode.attrs?.href || "https://";
                          const url = window.prompt("Edit URL:", prevUrl);
                          if (url === null) return;
                          editor.chain().focus().setNodeSelection(rowPos).updateAttributes(rowNode.type.name, { href: url.trim() !== '' ? url.trim() : null }).run();
                          setOpen(false);
                        }}
                        disabled={!editor}
                      />
                    </>
                  );
                })()}
              </MenuGroup>

              {/* Hide the divider for nodes whose menu only renders Delete
                  (currently just the spacer). Otherwise it shows up as a
                  lonely top border above Delete. */}
              {getNodeMenuKind(node) !== "spacer" && (
                <Separator orientation="horizontal" />
              )}

              <DeleteActionGroup editor={editor} />
            </ComboboxList>
          </MenuContent>
        </Menu>
      </ButtonGroup>
    </DragHandleAny>
  )
}
